import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { useWorkspace } from '../context/WorkspaceContext/useWorkspace';
import { useSettings } from '../context/SettingsContext/useSettings';
import { Filters } from './Filters';
import { FindLayersSettings } from './FindLayersSettings';
import { FindList } from './FindList';
import { IsadEntity } from './IsadEntity';
import { LpisEntity } from './LpisEntity';
import { Toolchain } from './Toolchain';
import { MarkerPointList } from './MarkerPointList';
import { FindPresetDialog, FindPresetDialogMode } from '../FindPresetDialog';
import { StoredFindFilter } from './Filters/types';
import { useCanUseFindPresets } from '../../hooks';
import { SmartSearch } from '../SmartSearch';

const FindToolbox = () => {
  const {
    store: { activeFindFilter, selectedEntity },
    addFindsFilter,
    replaceFindsFilter,
    updateFindsFilter,
    disableFindsFilter,
  } = useSettings();
  const {
    store: { markerPoints },
    hoveredMarkerId,
    setHoveredMarkerId,
    clearMarkerPoints,
    deleteMarkerPoint,
  } = useWorkspace();
  const canUsePresets = useCanUseFindPresets();
  const [presetMode, setPresetMode] = useState<FindPresetDialogMode | null>(
    null
  );
  const [filterUiVersion, setFilterUiVersion] = useState(0);

  const handleLoadPreset = (filter: StoredFindFilter | null) => {
    if (filter) {
      replaceFindsFilter(filter);
    } else {
      disableFindsFilter();
    }

    setFilterUiVersion((current) => current + 1);
  };

  const handlePresetClose = () => setPresetMode(null);
  const handleOpenLoadPreset = () => setPresetMode('load');
  const handleOpenSavePreset = () => setPresetMode('save');

  return (
    <Stack position="relative" gap={1}>
      <Toolchain />
      <SmartSearch />
      <FindLayersSettings />
      <Filters
        key={filterUiVersion}
        filter={activeFindFilter}
        onAdd={addFindsFilter}
        onChange={updateFindsFilter}
        onRemove={disableFindsFilter}
        enableImageFilter
        enableAmcrIdFilter
        enablePreset={canUsePresets}
        onLoadPreset={handleOpenLoadPreset}
        onSavePreset={handleOpenSavePreset}
      />
      {selectedEntity?.type === 'isad' && (
        <IsadEntity
          key={selectedEntity.id}
          id={selectedEntity.id}
          name={selectedEntity.name}
        />
      )}
      {selectedEntity?.type === 'lpis' && (
        <LpisEntity
          key={selectedEntity.id}
          parcelIds={selectedEntity.candidateIds}
          code={selectedEntity.code}
          userName={selectedEntity.userName}
          userId={selectedEntity.userId}
          areaHa={selectedEntity.areaHa}
          geometry={selectedEntity.geometry}
        />
      )}
      {selectedEntity?.type === 'find' && (
        <FindList selectedFind={selectedEntity.id} />
      )}
      {!!markerPoints && markerPoints.length > 0 && (
        <MarkerPointList
          hoveredMarkerId={hoveredMarkerId}
          onMarkerHover={setHoveredMarkerId}
          onMarkerPointsClear={clearMarkerPoints}
          onMarkerPointDelete={deleteMarkerPoint}
          markerPoints={markerPoints}
        />
      )}
      {canUsePresets && presetMode && (
        <FindPresetDialog
          mode={presetMode}
          open
          filter={activeFindFilter}
          onClose={handlePresetClose}
          onLoad={handleLoadPreset}
        />
      )}
    </Stack>
  );
};

export { FindToolbox };
